import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { passwordStrength } from '../passwordTools';
import { UserDetailState } from './useUserDetail';

/** The Password tab: a new password, typed or generated, temporary by default. */
export const UserPasswordTab: React.FC<{ state: UserDetailState }> = ({ state }) => {
  const { t } = useI18n();
  const { isLoading, newPassword, confirmPassword, showPassword } = state;
  const inputType = showPassword ? 'text' : 'password';
  const strength = passwordStrength(newPassword);

  return (
    <div className="tab-content ud-tab">
      <section className="cu-section" data-testid="form-user-password">
        <header className="cu-section-head">
          <h3>{t('users.resetPassword')}</h3>
          <p>{t('users.detail.passwordHint')}</p>
          <div className="cu-section-actions">
            <button type="button" className="btn btn-secondary btn-sm" disabled={isLoading} data-testid="ud-generate-password" onClick={state.fillGeneratedPassword}>
              {t('users.create.generate')}
            </button>
            <button type="button" className="cu-link" onClick={() => state.setShowPassword(!showPassword)} aria-pressed={showPassword}>
              {showPassword ? t('users.create.hide') : t('users.create.show')}
            </button>
          </div>
        </header>
        <div className="cu-grid">
          <div className="cu-field">
            <label htmlFor="ud-password">{t('users.newPassword')}</label>
            <input id="ud-password" type={inputType} className="cu-input mono" data-testid="field-user-password-new" value={newPassword}
                   onChange={(e) => state.setNewPassword(e.target.value)} placeholder={t('users.newPasswordPlaceholder')} disabled={isLoading} autoComplete="new-password" />
            {newPassword && (
              <span className={`cu-strength is-${strength}`} aria-label={t('users.create.strength')}>
                <i /><i /><i /><i />
                <span className="cu-strength-label">{t(`users.create.strength${strength}` as never)}</span>
              </span>
            )}
          </div>
          <div className="cu-field">
            <label htmlFor="ud-confirm">{t('users.confirmPassword')}</label>
            <input id="ud-confirm" type={inputType} className="cu-input mono" data-testid="field-user-password-confirm" value={confirmPassword}
                   onChange={(e) => state.setConfirmPassword(e.target.value)} placeholder={t('users.confirmNewPasswordPlaceholder')} disabled={isLoading} autoComplete="new-password" />
            {newPassword && confirmPassword && (
              newPassword === confirmPassword
                ? <span className="cu-hint is-ok">{t('users.create.passwordsMatch')}</span>
                : <span className="cu-error">{t('users.create.errPasswordMismatch')}</span>
            )}
          </div>
        </div>
        <label className="cu-check">
          <input type="checkbox" data-testid="field-user-password-temporary" checked={state.temporaryPassword} onChange={(e) => state.setTemporaryPassword(e.target.checked)} disabled={isLoading} />
          <span>{t('users.temporaryPassword')}</span>
        </label>
        <div className="ud-actions-right">
          <button type="button" className="btn btn-primary" data-testid="btn-user-reset-password" onClick={state.resetPassword} disabled={!state.canResetPassword}>
            {isLoading ? t('users.resetting') : t('users.resetPassword')}
          </button>
        </div>
      </section>
    </div>
  );
};
